/* eslint-disable */
/**
 * Isomorphic core for the Model Generations layer.
 *
 * Pure ESM, lodash-only (NO mongoose, NO DOM) so it can run on both the Node server
 * (`model/ModelGenerations.js`, via dynamic import) and the Vite admin-front client
 * (`useModelsData`).
 *
 * The computed data is encapsulated in the `ModelGenerationsData` class. Build an instance with one
 * of the static factories — `fromRows(...)` on the server (merges `model_gens` with
 * `model_gens_by_country` overrides: gen def -> wildcard '*' -> country, with facelift inheritance)
 * or `fromCompactData(...)` on the client (rehydrates the compact wire payload). All access goes
 * through instance methods; the internal index structures are private.
 *
 * Generation overrides are keyed by COUNTRY only (`country -> 'global' -> '*'`). Site
 * locale (es/en/pt) is NOT a generation-override dimension; it only gates model
 * availability and is handled outside this module.
 *
 * See `docs/2026-may-20 Generations, submodels and fuseboxes rules.md` for the rules.
 *
 * Compact wire format (v2) — designed to be small on an UNCOMPRESSED websocket:
 *   { v:2,
 *     m:  string[],                              // the ONE shared modelIds table (referenced by index everywhere)
 *     ng: number,                                // # of global gen defs; entries 0..ng-1 ARE gensById
 *     gmi: modelIdx[], gs: string[],             // genId split per gen = `@${m[gmi[i]]}@G${gs[i]}` (columnar, dense)
 *     gy|gt|gc: array[],                         // fromYear | toYear | codenames — DENSE positional (null/0 = absent)
 *     gn|gb: { [genIdx]: value },                // name | baseGenName — SPARSE (low density)
 *     rmap: { [genIdx]: genIdx },                // remappedToGenId, source gen -> target gen (both globals)
 *     bmk: { [modelIdx]: { [country]: genIdx[] } },   // declared gens per model+country
 *     tm|tf|ts: modelIdx[],                       // model | family | submodel type buckets
 *     rs|rm: { [modelIdx]: { [country]: modelIdx[] } },  // submodels | family-members (only where present)
 *     loc: { [siteLocale]: modelIdx[] },          // availability
 *     makes: string[] }
 * What is NOT shipped (and why): `baseModelId` comes from `m[gmi[i]]`; `gen`/`baseGenId`/`baseGen`
 * are re-derived from the reconstructed genId; `gensById` needs no index map (entries 0..ng-1);
 * `modelIdsByGenId` is server-only (no client consumer of getModelIdsForGenId). The decode
 * reconstructs the exact same in-memory structures, so the instance methods are identical on
 * server and client.
 */
import _ from 'lodash';

// Internal composite-key separator. modelIds and genIds both contain '-'/'@'/'.', so use a
// control char that cannot appear in them. Used consistently for build AND query, so the
// exact char only needs to be stable within this module.
const SEP = String.fromCharCode(1);
const mk = (modelId, country) => `${modelId}${SEP}${country}`;
const mkg = (modelId, country, genId) => `${modelId}${SEP}${country}${SEP}${genId}`;

export function isFacelift(genId) {
  return genId.match(/@G[a-z]*\d+[a-z]*\.\d+[a-z]*$/i);
}

export function getBaseGenId(genId) {
  // TODO: Assuming model ids never ever have a "."
  return genId.split('.')[0];
}

export const isGeneration = str => str[0] === '@';

const removeNulls = gen => _.pickBy(gen);

/**
 * Facelifts inherit photo/specs/fromYear/toYear from their (computed) base generation.
 * Facelift-specific values always take precedence.
 */
function inheritPropertiesFromBaseGen(baseGen, facelift) {
  if (!baseGen) {
    return facelift;
  }

  // Merge base gen specs with current facelift specs
  let specs = { ...baseGen.specs, ...facelift.specs };

  let res = {
    ...facelift,
    baseGen: baseGen.gen,
    baseGenId: baseGen.genId,
    specs,
  };

  if (_.isEmpty(specs)) {
    delete res.specs;
  }

  if (baseGen.name) {
    res.baseGenName = baseGen.name;
  }

  for (const prop of ['photo', 'miniatureId', 'fromYear', 'toYear']) {
    if (res[prop] === undefined && baseGen[prop] !== undefined) {
      res[prop] = baseGen[prop];
    }
  }

  return res;
}

/**
 * A small string -> int registry that appends on miss. Reused across the whole endpoint so a
 * single `modelIds` / `genIds` table backs every compact sub-structure (no duplicated tables).
 */
function makeIndexer(existing) {
  const table = existing ? existing.slice() : [];
  const map = new Map(table.map((s, i) => [s, i]));
  return {
    idx(str) {
      let i = map.get(str);
      if (i === undefined) {
        i = table.length;
        table.push(str);
        map.set(str, i);
      }
      return i;
    },
    table() {
      return table;
    },
  };
}

// A genId is structurally `@${baseModelId}@G${gen}` (modelIds never contain '@'). So `baseModelId`,
// `gen`, and (for facelifts) `baseGenId`/`baseGen` are pure functions of the genId and need not be
// shipped — they are reconstructed on hydrate. `fromRows` verification asserts this is lossless.
function parseGenId(genId) {
  if (typeof genId !== 'string' || genId[0] !== '@') return null;
  const at = genId.indexOf('@G', 1);
  if (at < 0) return null;
  return { baseModelId: genId.slice(1, at), gen: genId.slice(at + 2) };
}

// The only gen fields that are NOT derivable from the genId and that the client consumes
// (validator year ranges, editor/TrimSpec display, timeline). Heavy Mixed blobs (specs, photo, …)
// are intentionally excluded from the wire. `codenames` is omitted when empty (the common case) and
// restored to `[]` on hydrate. The dedup key is built from exactly these stored fields.
// (`remappedToGenId` is NOT here — it's shipped separately as `rmap` and re-attached on decode.)
function storedGenSubset(def) {
  const s = { genId: def.genId };
  if (def.name !== undefined) s.name = def.name;
  if (def.codenames && def.codenames.length) s.codenames = def.codenames;
  if (def.fromYear !== undefined) s.fromYear = def.fromYear;
  if (def.toYear !== undefined) s.toYear = def.toYear;
  if (def.baseGenName !== undefined) s.baseGenName = def.baseGenName;
  return s;
}

// Inverse of the encode: rebuild the full wire gen object from its genId + stored sparse fields,
// re-deriving baseModelId/gen/baseGenId/baseGen from the genId.
function reconstructGen(genId, { name, codenames, fromYear, toYear, baseGenName } = {}) {
  const out = { genId };
  const parsed = parseGenId(genId);
  if (parsed) {
    out.gen = parsed.gen;
    out.baseModelId = parsed.baseModelId;
  }
  out.codenames = codenames || [];
  if (name !== undefined) out.name = name;
  if (fromYear !== undefined) out.fromYear = fromYear;
  if (toYear !== undefined) out.toYear = toYear;
  if (isFacelift(genId)) {
    const baseGenId = getBaseGenId(genId);
    out.baseGenId = baseGenId;
    const baseParsed = parseGenId(baseGenId);
    if (baseParsed) out.baseGen = baseParsed.gen;
    if (baseGenName !== undefined) out.baseGenName = baseGenName;
  }
  return out;
}

// Deterministic stringify (sorted keys) for deduping identical merged gen objects.
function stableStringify(value) {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  const keys = Object.keys(value).sort();
  return `{${keys.map(k => `${JSON.stringify(k)}:${stableStringify(value[k])}`).join(',')}}`;
}

/**
 * Pure reimplementation of the original `ModelGenerations.initialize` merge algorithm over plain
 * rows. Rows must already be `.lean()` plain objects; `model_gens_by_country` rows must already be
 * filtered (state:'published' AND non-empty generations / wasSold:false) by the caller.
 *
 * @returns {{ gensById, byModelKey: Record<string, Record<string, object[]>>, byModelKeyGen, modelIdsByGenId }}
 */
function buildIndexes(modelGenRows, modelGenByCountryRows) {
  let gensById = _.keyBy(modelGenRows.map(removeNulls), 'genId');
  const faceliftsByGen = {};

  // Facelifts inherit some of their base generation properties
  gensById = _.mapValues(gensById, (gen, genId) => {
    if (isFacelift(genId)) {
      const baseGenId = getBaseGenId(genId);
      if (gensById[baseGenId]) {
        faceliftsByGen[baseGenId] = (faceliftsByGen[baseGenId] || new Set()).add(genId);
        return inheritPropertiesFromBaseGen(gensById[baseGenId], gen);
      }
      // Orphan facelift (base gen missing) -> drop it
      return undefined;
    }
    return gen;
  });
  gensById = _.omitBy(gensById, _.isUndefined);

  let genCountries = modelGenByCountryRows.map(removeNulls);

  const byModelKey = {};
  const byModelKeyGen = {};
  let modelIdsByGenId = {};

  // Sort so that '*' (wildcard defaults) is processed first, then 'global', then other countries.
  genCountries = _.sortBy(genCountries, ({ countries: [c] }) => (c === '*' ? 0 : c === 'global' ? 1 : 2));

  const wildcardDefaults = {};

  for (const { modelId, countries, generations, specs } of genCountries) {
    if (countries[0] === '*') {
      wildcardDefaults[modelId] = { specs };
      for (const gen of generations) {
        const { genId, ...genDefaults } = gen;
        wildcardDefaults[mk(modelId, genId)] = genDefaults;
      }
    }

    for (const country of countries) {
      // Ensure base generations are processed before facelifts so facelifts can inherit
      const sortedGenerations = _.sortBy(generations.map(removeNulls), ({ genId }) => (isFacelift(genId) ? 1 : 0));

      const getGen = (genId, c) => _.find(byModelKey[modelId]?.[c], { genId });

      (byModelKey[modelId] = byModelKey[modelId] || {})[country] = [];
      for (const { specs: genSpecs, genId, ...props } of sortedGenerations) {
        const genDef = gensById[genId];
        if (!genDef) {
          // Using an undefined genId in a country definition -> skip
          continue;
        }

        const genDefault = wildcardDefaults[mk(modelId, genId)] || {};

        const mergedSpecs = {
          ...genDef?.specs,
          ...wildcardDefaults[modelId]?.specs,
          ...genDefault.specs,
          ...specs,
          ...genSpecs,
        };

        let countryGen = { genId, ...genDef, ...genDefault, ...props, specs: mergedSpecs };

        if (isFacelift(genId)) {
          const id = getBaseGenId(genId);
          const baseGen = getGen(id, country) || getGen(id, '*') || gensById[id];
          countryGen = inheritPropertiesFromBaseGen(baseGen, countryGen);
        }

        if (_.isEmpty(countryGen.specs)) {
          delete countryGen.specs;
        }

        byModelKeyGen[mkg(modelId, country, genId)] = countryGen;
        byModelKey[modelId][country].push(countryGen);

        modelIdsByGenId[genId] = modelIdsByGenId[genId] || new Set();
        modelIdsByGenId[genId].add(modelId);
      }

      // Inherit base-gen country props into facelifts not explicitly declared for this country
      for (const gen of byModelKey[modelId][country]) {
        for (const faceId of faceliftsByGen[gen.genId] || []) {
          if (!byModelKeyGen[mkg(modelId, country, faceId)]) {
            const facelift = byModelKeyGen[mkg(modelId, '*', faceId)] || gensById[faceId];
            byModelKeyGen[mkg(modelId, country, faceId)] = inheritPropertiesFromBaseGen(gen, facelift);
          }
        }
      }
    }
  }

  // Link base gen modelIds to facelifts and vice-versa
  for (const [genId, modelIds] of Object.entries(modelIdsByGenId)) {
    if (isFacelift(genId)) {
      const baseGenId = getBaseGenId(genId);
      for (const modelId of modelIds) {
        modelIdsByGenId[baseGenId] = modelIdsByGenId[baseGenId] || new Set();
        modelIdsByGenId[baseGenId].add(modelId);
      }
    } else {
      for (const faceliftId of faceliftsByGen[genId] || []) {
        for (const modelId of modelIds) {
          modelIdsByGenId[faceliftId] = modelIdsByGenId[faceliftId] || new Set();
          modelIdsByGenId[faceliftId].add(modelId);
        }
      }
    }
  }
  modelIdsByGenId = _.mapValues(modelIdsByGenId, ids => Array.from(ids));

  return { gensById, byModelKey, byModelKeyGen, modelIdsByGenId };
}

/**
 * Server-only: attach a resolved `miniature` object onto every gen def that carries a `miniatureId`.
 * `miniaturesById` is a plain `{ [miniatureIdString]: { url, dimensions, overlayBox, imgW, imgH } }` map already
 * resolved by the caller (this module stays mongoose/DOM-free). The merged country/facelift gens in
 * `byModelKeyGen` already inherit `miniatureId` (country override > '*' defaults > base, and base ->
 * facelift), so resolving across `gensById` + `byModelKeyGen` covers everything getGenDef()/gens()
 * return. `miniature` is NOT part of the compact wire subset, so it never ships to the client.
 */
function attachMiniatures(indexes, miniaturesById) {
  if (!miniaturesById) return;
  const attach = (gen) => {
    if (gen && gen.miniatureId != null) {
      const mini = miniaturesById[String(gen.miniatureId)];
      if (mini) gen.miniature = mini;
    }
  };
  for (const gen of Object.values(indexes.gensById)) attach(gen);
  for (const gen of Object.values(indexes.byModelKeyGen)) attach(gen);
}

/**
 * Encode the gens portion of the compact wire payload (v2). Deduped, columnar table of merged gen
 * objects referenced by integer index; only non-derivable, client-consumed fields are stored (see
 * `storedGenSubset`). Global definitions (`gensById`) are interned FIRST, so they occupy entries
 * `0..ng-1` aligned with their genId — this lets the client rebuild `gensById` without an index map.
 * In-memory `byModelKey` is `{ modelId: { country: [gen] } }`; on the wire (`bmk`) it is
 * `{ modelIdx: { country: [genTableIdx] } }`, sharing the given `modelIdx`.
 * `modelIdsByGenId` is intentionally NOT shipped (server-only; no client consumer of
 * `getModelIdsForGenId`). @returns {{ ng, gmi, gs, gy, gt, gc, gn, gb, rmap, bmk }}
 */
function encodeGens(indexes, modelIdx) {
  const stored = [];
  const indexByJson = new Map();
  const intern = (def) => {
    const s = storedGenSubset(def);
    const json = stableStringify(s);
    let i = indexByJson.get(json);
    if (i === undefined) {
      i = stored.length;
      stored.push(s);
      indexByJson.set(json, i);
    }
    return i;
  };

  // Globals first → entries 0..ng-1 ARE gensById, in iteration order (each has a distinct genId, so
  // none dedups against another global). Country variants interned after may reuse a global slot.
  for (const def of Object.values(indexes.gensById)) intern(def);
  const ng = stored.length;

  // remappedToGenId lives only on global gens (a model_gens field). Ship it as a sparse map of
  // gens-table indices `{ sourceGenIdx: targetGenIdx }` — both globals (0..ng-1) — instead of a
  // repeated genId string. The target is an existing gen (GenerationRemapService guarantees it).
  const globalIdxByGenId = {};
  for (let i = 0; i < ng; i++) globalIdxByGenId[stored[i].genId] = i;
  const rmap = {};
  for (const def of Object.values(indexes.gensById)) {
    if (!def.remappedToGenId) continue;
    const from = globalIdxByGenId[def.genId];
    const to = globalIdxByGenId[def.remappedToGenId];
    if (from !== undefined && to !== undefined) rmap[from] = to;
  }

  const bmk = {};
  for (const [modelId, byCountry] of Object.entries(indexes.byModelKey)) {
    const mi = modelIdx.idx(modelId);
    for (const [country, arr] of Object.entries(byCountry)) {
      (bmk[mi] = bmk[mi] || {})[country] = arr.map(intern);
    }
  }

  // Columnar. genId is split into its baseModelId (an index into the SHARED m table — most are
  // already there from bmk/relations; the rest are appended here) plus the `@G…` suffix `gs`. Dense
  // fields (fromYear ~100%, toYear ~80%, codenames ~87%) are positional arrays (null/0 = absent);
  // genuinely sparse fields (name ~33%, baseGenName ~0.5%) stay as index-keyed maps.
  const gmi = []; const gs = [];
  const gy = []; const gt = []; const gc = [];
  const gn = {}; const gb = {};
  stored.forEach((s, i) => {
    const p = parseGenId(s.genId);
    gmi.push(modelIdx.idx(p ? p.baseModelId : s.genId));
    gs.push(p ? p.gen : s.genId);
    gy.push(s.fromYear === undefined ? null : s.fromYear);
    gt.push(s.toYear === undefined ? null : s.toYear);
    gc.push(s.codenames && s.codenames.length ? s.codenames : 0);
    if (s.name !== undefined) gn[i] = s.name;
    if (s.baseGenName !== undefined) gb[i] = s.baseGenName;
  });

  return { ng, gmi, gs, gy, gt, gc, gn, gb, rmap, bmk };
}

// Transform the in-memory relations (keyed by the shared modelIdx) into the compact wire shape:
// three model-type buckets (replacing the doubly-shipped typeByModelIdx) and submodel/family-member
// maps that only carry models which actually have relations. `loc` (availabilityByLocale) passes
// through unchanged (already `{ locale: modelIdx[] }`).
function encodeRelations(relations) {
  const tm = []; const tf = []; const ts = [];
  for (const [miStr, type] of Object.entries(relations.typeByModelIdx || {})) {
    const mi = Number(miStr);
    if (type === 'families') tf.push(mi);
    else if (type === 'submodels') ts.push(mi);
    else tm.push(mi);
  }

  const rs = {}; const rm = {};
  for (const [miStr, entry] of Object.entries(relations.byModelIdx || {})) {
    for (const [country, rel] of Object.entries(entry.byCountry || {})) {
      if (rel.submodels && rel.submodels.length) (rs[miStr] = rs[miStr] || {})[country] = rel.submodels;
      if (rel.members && rel.members.length) (rm[miStr] = rm[miStr] || {})[country] = rel.members;
    }
  }

  return { tm, tf, ts, rs, rm, loc: relations.availabilityByLocale || {} };
}

/**
 * Rebuild the gen query indexes from the compact wire payload. Produces the same structure shape
 * that `buildIndexes` does (minus `modelIdsByGenId`, which is not shipped), so the instance methods
 * work identically on both sides. baseModelId/gen/baseGenId/baseGen are re-derived from each genId.
 */
function decodeGens(p, modelIds) {
  const { gmi = [], gs = [], gn = {}, gc = [], gy = [], gt = [], gb = {}, rmap = {}, ng = 0, bmk = {} } = p;

  const gens = gmi.map((mi, i) => {
    const genId = `@${modelIds[mi]}@G${gs[i]}`;
    return reconstructGen(genId, {
      name: gn[i],
      codenames: Array.isArray(gc[i]) ? gc[i] : undefined,
      fromYear: gy[i] == null ? undefined : gy[i],
      toYear: gt[i] == null ? undefined : gt[i],
      baseGenName: gb[i],
    });
  });

  // Re-attach remappedToGenId (gens-table index -> target genId) onto the source gen objects.
  for (const [from, to] of Object.entries(rmap)) {
    const src = gens[Number(from)];
    const tgt = gens[Number(to)];
    if (src && tgt) src.remappedToGenId = tgt.genId;
  }

  const gensById = {};
  for (let i = 0; i < ng; i++) gensById[gens[i].genId] = gens[i];

  const byModelKey = {};
  const byModelKeyGen = {};
  for (const [miStr, byCountry] of Object.entries(bmk)) {
    const modelId = modelIds[Number(miStr)];
    byModelKey[modelId] = {};
    for (const [country, idxArr] of Object.entries(byCountry)) {
      const arr = idxArr.map(i => gens[i]);
      byModelKey[modelId][country] = arr;
      for (const def of arr) byModelKeyGen[mkg(modelId, country, def.genId)] = def;
    }
  }

  return { gensById, byModelKey, byModelKeyGen, modelIdsByGenId: {} };
}

// Rebuild the in-memory relations structure consumed by the instance methods from the wire shape.
function decodeRelations(p) {
  const { tm = [], tf = [], ts = [], rs = {}, rm = {}, loc = {} } = p;

  const typeByModelIdx = {};
  for (const mi of tm) typeByModelIdx[mi] = 'models';
  for (const mi of tf) typeByModelIdx[mi] = 'families';
  for (const mi of ts) typeByModelIdx[mi] = 'submodels';

  const byModelIdx = {};
  const ensure = (mi, country) => {
    const e = (byModelIdx[mi] = byModelIdx[mi] || { byCountry: {} });
    return (e.byCountry[country] = e.byCountry[country] || {});
  };
  for (const [mi, byC] of Object.entries(rs)) for (const [c, arr] of Object.entries(byC)) ensure(mi, c).submodels = arr;
  for (const [mi, byC] of Object.entries(rm)) for (const [c, arr] of Object.entries(byC)) ensure(mi, c).members = arr;

  return { byModelIdx, availabilityByLocale: loc, typeByModelIdx };
}

// Builds the compact model-relations + availability structures from raw `modelos` docs, sharing the
// given (already gens-seeded) modelId indexer so the whole payload references ONE modelIds table.
// `submodels` are the direct inverse of `parent`; `members` are a family's direct members;
// availability is kept compact by (site) locale; type is the wildcard ('*') model type.
function buildRelations(modelObjects, modelIdx) {
  const TYPE_FIELD = { family: 'families', model: 'models', submodel: 'submodels' };
  const typeByModelIdx = {};
  const availabilityByLocale = {};
  const submodelsByParentCountry = {}; // `${parentId}${SEP}${country}` -> Set(childId)
  const membersByFamilyCountry = {}; // `${familyId}${SEP}${country}` -> [memberId]

  for (const m of modelObjects) {
    if (!m || m.toDelete) continue;
    const id = m.idModelo;
    const mi = modelIdx.idx(id);

    const starType = m.typeByCountry && m.typeByCountry['*'] && m.typeByCountry['*'].type;
    if (starType) typeByModelIdx[mi] = TYPE_FIELD[starType] || 'models';

    for (const loc of m.locales || []) {
      (availabilityByLocale[loc] = availabilityByLocale[loc] || []).push(mi);
    }

    for (const [country, t] of Object.entries(m.typeByCountry || {})) {
      if (!t) continue;
      if (t.type === 'submodel' && t.parent) {
        const key = `${t.parent}${SEP}${country}`;
        (submodelsByParentCountry[key] = submodelsByParentCountry[key] || new Set()).add(id);
      }
      if (t.type === 'family' && t.members && t.members.length) {
        membersByFamilyCountry[`${id}${SEP}${country}`] = t.members;
      }
    }
  }

  const byModelIdx = {};
  const ensureCountry = (mi, country) => {
    const e = (byModelIdx[mi] = byModelIdx[mi] || { byCountry: {} });
    return (e.byCountry[country] = e.byCountry[country] || {});
  };

  for (const [key, set] of Object.entries(submodelsByParentCountry)) {
    const sepPos = key.indexOf(SEP);
    ensureCountry(modelIdx.idx(key.slice(0, sepPos)), key.slice(sepPos + 1)).submodels = Array.from(set).map(s => modelIdx.idx(s));
  }
  for (const [key, members] of Object.entries(membersByFamilyCountry)) {
    const sepPos = key.indexOf(SEP);
    ensureCountry(modelIdx.idx(key.slice(0, sepPos)), key.slice(sepPos + 1)).members = members.map(s => modelIdx.idx(s));
  }
  for (const [miStr, type] of Object.entries(typeByModelIdx)) {
    (byModelIdx[miStr] = byModelIdx[miStr] || { byCountry: {} }).type = type;
  }

  return { byModelIdx, availabilityByLocale, typeByModelIdx };
}

function uniqueMakes(modelObjects) {
  const set = new Set();
  for (const m of modelObjects) {
    if (m && !m.toDelete && m.marca) set.add(m.marca);
  }
  return Array.from(set);
}

/**
 * Computed models data for the admin layer — the single source of truth for the models/generations
 * compact format, in both directions. Construct via the static factories; query via instance
 * methods. All underlying structures are private — callers never touch the compact format directly.
 *
 * On the server it is built from DB rows + `modelos` docs (`fromRows`); `toCompactData()` then yields
 * the entire wire payload. On the client it is rebuilt from that payload (`fromCompactData(res)`) and
 * is the object returned by the `useModelsData` hook, exposing everything the UI needs: generation
 * queries, model lists (makes/models/families/submodels), model type/locale availability, and model
 * relations (submodels/family members). `fromRows` <-> `toCompactData` <-> `fromCompactData` are
 * symmetric: the producer and consumer share one format owned entirely here.
 */
export class ModelGenerationsData {
  // Generation indexes
  #gensById;
  #byModelKey;
  #byModelKeyGen;
  #modelIdsByGenId;
  #compact;

  // Model relations / availability / makes (client instances only)
  #makes;
  #modelIds;
  #byModelIdx;
  #availabilityByLocale;
  #typeByModelIdx;
  #modelIdxByModelId;
  #flatByType; // lazy cache
  #modelsByLocale; // lazy cache

  /** @private — use the static factories instead. */
  constructor({ indexes, compact = null, makes = [], modelIds = [], relations = null }) {
    this.#gensById = indexes.gensById;
    this.#byModelKey = indexes.byModelKey;
    this.#byModelKeyGen = indexes.byModelKeyGen;
    this.#modelIdsByGenId = indexes.modelIdsByGenId;
    this.#compact = compact;

    const rel = relations || { byModelIdx: {}, availabilityByLocale: {}, typeByModelIdx: {} };
    this.#makes = makes;
    this.#modelIds = modelIds;
    this.#byModelIdx = rel.byModelIdx || {};
    this.#availabilityByLocale = rel.availabilityByLocale || {};
    this.#typeByModelIdx = rel.typeByModelIdx || {};

    this.#modelIdxByModelId = {};
    modelIds.forEach((id, i) => { this.#modelIdxByModelId[id] = i; });
  }

  /**
   * Build from raw DB rows (server side). Computes the full compact wire payload (generations +
   * relations + makes, all sharing one modelIds table), available afterwards via `toCompactData()`.
   * @param {object[]} modelGenRows          plain `model_gens` rows (.lean())
   * @param {object[]} modelGenByCountryRows plain, already-filtered `model_gens_by_country` rows
   * @param {object[]} [modelObjects]        plain `modelos` docs (idModelo/marca/typeByCountry/locales)
   * @param {Object<string, {url:string,dimensions:object,overlayBox:object,imgW:number,imgH:number}>} [miniaturesById]
   *        server-only resolved miniatures keyed by miniature _id string; attached as `gen.miniature`
   *        (never shipped on the wire). Omit on the client.
   * @returns {ModelGenerationsData}
   */
  static fromRows(modelGenRows, modelGenByCountryRows, modelObjects = [], miniaturesById = null) {
    const indexes = buildIndexes(modelGenRows, modelGenByCountryRows);
    attachMiniatures(indexes, miniaturesById);

    // ONE shared modelIds table: gens populate it (via byModelKey), then relations extend it.
    const modelIdx = makeIndexer();
    const gensWire = encodeGens(indexes, modelIdx);
    const relations = buildRelations(modelObjects, modelIdx);
    const relWire = encodeRelations(relations);
    const makes = uniqueMakes(modelObjects);
    const modelIds = modelIdx.table();

    const compact = { v: 2, m: modelIds, ...gensWire, ...relWire, makes };
    return new ModelGenerationsData({ indexes, compact, makes, modelIds, relations });
  }

  /**
   * Rehydrate from the compact wire payload (client side). The argument is exactly what
   * `toCompactData()` produces (the v2 columnar/index-shared format documented at the top of this
   * file). `modelIdsByGenId` is not carried on the wire, so `getModelIdsForGenId` returns [] on
   * client instances (it has no client consumer).
   * @param {object} [payload]
   * @returns {ModelGenerationsData}
   */
  static fromCompactData(payload = {}) {
    const p = payload || {};
    const modelIds = p.m || [];
    const indexes = p.gmi
      ? decodeGens(p, modelIds)
      : { gensById: {}, byModelKey: {}, byModelKeyGen: {}, modelIdsByGenId: {} };
    const relations = p.gmi ? decodeRelations(p) : null;
    return new ModelGenerationsData({ indexes, compact: p, makes: p.makes || [], modelIds, relations });
  }

  /** An empty instance (e.g. before data has loaded on the client). @returns {ModelGenerationsData} */
  static empty() {
    return ModelGenerationsData.fromCompactData({});
  }

  // ---- generation queries ----

  /**
   * Strict generations declared for a model at a country; union of all countries when country is omitted.
   * @param {string} modelId
   * @param {string|null} [country]
   * @returns {object[]}
   */
  gens(modelId, country = null) {
    const byCountry = this.#byModelKey[modelId];
    if (!byCountry) return [];

    if (country != null) {
      return byCountry[country] || byCountry.global || [];
    }

    const seen = new Set();
    const result = [];
    for (const [country, countryGens] of Object.entries(byCountry)) {
      for (const gen of countryGens) {
        if (!seen.has(gen.genId)) {
          seen.add(gen.genId);
          gen.countries = new Set([country]);
          result.push(gen);
        } else {
          _.find(result, { genId: gen.genId }).countries.add(country);
        }
      }
    }
    return result;
  }

  /**
   * Most-specific gen definition for a (genId, modelId, country); facelift falls back to base gen.
   * @param {string} genId
   * @param {{ modelId?: string, country?: string }} [opts]
   * @returns {object|undefined}
   */
  getGenDef(genId, { modelId, country = '*' } = {}) {
    const res = this.#byModelKeyGen[mkg(modelId, country, genId)]
      || this.#byModelKeyGen[mkg(modelId, 'global', genId)]
      || this.#byModelKeyGen[mkg(modelId, '*', genId)]
      || this.#gensById[genId];

    if (!res && isFacelift(genId)) {
      return this.getGenDef(getBaseGenId(genId), { modelId, country });
    }
    return res;
  }

  /**
   * Base (model-agnostic) gen definition for a genId, WITH the model's `*` inherited-props defaults
   * applied. The `*` entry of `model_gens_by_country` carries default props (years, name, …) that a
   * gen inherits regardless of country; `buildIndexes` already merges them into the `*` variant
   * (base def overlaid with the `*` defaults), so when one exists for the genId's own baseModelId we
   * return it. Otherwise we return the raw catalog def. This is why a gen whose years live only in
   * its `*` defaults (not its base `model_gens` doc) still reports them here.
   * @param {string} genId
   * @returns {object|undefined}
   */
  getBaseGenDef(genId) {
    const base = this.#gensById[genId];
    const parsed = parseGenId(genId);
    const starMerged = parsed && this.#byModelKeyGen[mkg(parsed.baseModelId, '*', genId)];
    if (!starMerged) return base;
    // `remappedToGenId` is a base-only field; carry it through the `*` overlay if present.
    return base && base.remappedToGenId && !starMerged.remappedToGenId
      ? { ...starMerged, remappedToGenId: base.remappedToGenId }
      : starMerged;
  }

  /**
   * All modelIds that reference a genId (including base<->facelift links).
   * @param {string} genId
   * @returns {string[]}
   */
  getModelIdsForGenId(genId) {
    return this.#modelIdsByGenId[genId] || [];
  }

  /**
   * All collection-level (global) generation definitions, as a flat array.
   * @returns {object[]}
   */
  generations() {
    return Object.values(this.#gensById);
  }

  // ---- model lists / type / availability (client instances) ----

  /**
   * All makes (brands).
   * @returns {string[]}
   */
  makes() {
    return this.#makes;
  }

  /**
   * The full shared modelIds table.
   * @returns {string[]}
   */
  allModelIds() {
    return this.#modelIds;
  }

  #flat() {
    if (!this.#flatByType) {
      const flat = { models: [], families: [], submodels: [] };
      for (const [miStr, field] of Object.entries(this.#typeByModelIdx)) {
        const modelId = this.#modelIds[Number(miStr)];
        if (modelId && flat[field]) flat[field].push(modelId);
      }
      this.#flatByType = flat;
    }
    return this.#flatByType;
  }

  /** modelIds whose wildcard type is a plain model. @returns {string[]} */
  models() { return this.#flat().models; }
  /** modelIds whose wildcard type is a family. @returns {string[]} */
  families() { return this.#flat().families; }
  /** modelIds whose wildcard type is a submodel. @returns {string[]} */
  submodels() { return this.#flat().submodels; }

  /**
   * The wildcard type of a modelId.
   * @param {string} modelId
   * @returns {'models'|'families'|'submodels'|undefined}
   */
  modelTypeOf(modelId) {
    const mi = this.#modelIdxByModelId[modelId];
    return mi === undefined ? undefined : this.#typeByModelIdx[mi];
  }

  /**
   * The Set of modelIds available in a (site) locale, or null if the locale has no availability
   * data (in which case callers should not treat absence as "unavailable").
   * @param {string} locale
   * @returns {Set<string>|null}
   */
  modelsInLocale(locale) {
    if (!this.#modelsByLocale) {
      const byLocale = {};
      for (const [loc, idxArr] of Object.entries(this.#availabilityByLocale)) {
        const set = new Set();
        for (const i of idxArr) {
          const id = this.#modelIds[i];
          if (id) set.add(id);
        }
        byLocale[loc] = set;
      }
      this.#modelsByLocale = byLocale;
    }
    return this.#modelsByLocale[locale] || null;
  }

  // ---- model relation queries (country-aware; country -> '*' -> union of all countries) ----

  #resolveRelation(modelId, country, prop) {
    const entry = this.#byModelIdx[this.#modelIdxByModelId[modelId]];
    if (!entry || !entry.byCountry) return [];
    const bc = entry.byCountry;
    let arr;
    if (country && bc[country] && bc[country][prop]) {
      arr = bc[country][prop];
    } else if (bc['*'] && bc['*'][prop]) {
      arr = bc['*'][prop];
    } else {
      const set = new Set();
      for (const c of Object.values(bc)) {
        for (const i of c[prop] || []) set.add(i);
      }
      arr = Array.from(set);
    }
    return arr.map(i => this.#modelIds[i]).filter(Boolean);
  }

  /**
   * Direct submodels of a model (inverse of `parent`).
   * @param {string} modelId
   * @param {string} [country]
   * @returns {string[]}
   */
  submodelsOf(modelId, country) {
    return this.#resolveRelation(modelId, country, 'submodels');
  }

  /**
   * Direct members of a family.
   * @param {string} modelId
   * @param {string} [country]
   * @returns {string[]}
   */
  familyMembersOf(modelId, country) {
    return this.#resolveRelation(modelId, country, 'members');
  }

  /**
   * The compact wire payload (v2 — see the format notes at the top of this file). This is exactly
   * the shape `fromCompactData` consumes. Available on instances built via `fromRows` or
   * `fromCompactData`.
   * @returns {object}
   */
  toCompactData() {
    if (!this.#compact) {
      throw new Error('ModelGenerationsData has no compact representation (build it via fromRows/fromCompactData)');
    }
    return this.#compact;
  }
}
